// Payment Tracking convergence — shared formatting + status-token mapping
// for the servicing ledger surfaces. Money renders $ 2dp with separators,
// negatives in parentheses, per the module's financial-figure ruleset.

import { formatCurrency } from "@/design-system/lib/format";
import type { StatusTone } from "@/design-system/components/ui";
import type {
  ServicingAllocationLineDto,
  ServicingPeriodStatus,
  ServicingTraceStatus,
} from "@/api/portfolioAdmin/servicingApi";

export function fmtMoney(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return "—";
  const abs = formatCurrency(Math.abs(amount), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `(${abs})` : abs;
}

export const PERIOD_STATUS_LABEL: Record<ServicingPeriodStatus, string> = {
  upcoming: "Upcoming",
  "on-time": "On time",
  late: "Late",
  partial: "Partial",
  missed: "Missed",
  nsf: "NSF",
  prepaid: "Prepaid",
};

/** RAG mapping: green = satisfied, amber = at risk, red = overdue/breach. */
export function periodStatusTone(status: ServicingPeriodStatus): StatusTone {
  switch (status) {
    case "on-time":
    case "prepaid":
      return "ok";
    case "late":
    case "partial":
      return "warn";
    case "missed":
    case "nsf":
      return "danger";
    default:
      return "neutral";
  }
}

export const TRACE_STATUS_LABEL: Record<ServicingTraceStatus, string> = {
  fired: "Paid",
  zero: "Zero",
  skipped: "Skipped",
  not_applicable: "N/A",
  reversed: "Reversed",
};

export function traceStatusTone(status: ServicingTraceStatus): StatusTone {
  switch (status) {
    case "fired":
      return "ok";
    case "reversed":
      return "danger";
    default:
      return "muted";
  }
}

/** How a disbursement settles. "" when not a disbursement (callers render
 *  their own placeholder); FCI executes servicer-settled lines directly. */
export function settlementLabel(
  channel: ServicingAllocationLineDto["settlementChannel"],
): string {
  switch (channel) {
    case "servicer":
      return "Servicer (FCI)";
    case "phoenix":
      return "Phoenix";
    default:
      return "";
  }
}
